/**
 * useWidgetData - Generic Widget Data Hook
 *
 * Custom hook wrapping TanStack Query (React Query) for widget data fetching.
 * Provides automatic caching, stale-while-revalidate, configurable refetch intervals,
 * retry with exponential backoff, and error handling.
 *
 * Tracks whether data came from a live API or mock fallback via isLive flag.
 *
 * Replaces the manual timer-based refresh logic in WidgetWrapper.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import { useRef } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { WidgetInstance } from '../types';
import { useOptionalDashboard } from '../DashboardContext';
import { effectiveRefreshSeconds } from '../utils/refreshSettings';

/**
 * Options for useWidgetData hook
 */
interface UseWidgetDataOptions<T> {
  /** Unique key for the widget data (used as React Query cache key) */
  widgetId: string;
  /** The widget instance (for refresh interval settings) */
  instance: WidgetInstance;
  /** Async function that fetches the data */
  fetchFn: () => Promise<T>;
  /** Override the refresh interval from widget settings (in seconds) */
  refreshIntervalOverride?: number;
  /** Whether this query is enabled (default: true) */
  enabled?: boolean;
  /** Stale time in milliseconds (default: 10 seconds) */
  staleTime?: number;
  /** Additional React Query options */
  queryOptions?: Partial<UseQueryOptions<T, Error>>;
}

/**
 * Return type with added convenience properties
 */
type UseWidgetDataResult<T> = UseQueryResult<T, Error> & {
  /** Trigger a manual refresh */
  refresh: () => void;
  /** Whether this is the first load (no cached data) */
  isFirstLoad: boolean;
  /** Formatted last updated time string */
  lastUpdatedText: string;
  /** Whether data was fetched from a live API (not mock fallback) */
  isLive: boolean;
};

/**
 * Create a fetch wrapper that tracks whether the API call succeeded (live)
 * or fell back to mock data.
 */
function createTrackedFetchFn<T>(
  fetchFn: () => Promise<T>,
  liveRef: React.MutableRefObject<boolean>
): () => Promise<T> {
  return async () => {
    try {
      const result = await fetchFn();
      // If fetchFn didn't throw, it's live data
      // (widgets that use try/catch with mock fallback will set isLive=false
      //  since the outer fetchFn succeeds either way - we detect via meta)
      liveRef.current = true;
      return result;
    } catch {
      liveRef.current = false;
      throw new Error('Fetch failed');
    }
  };
}

/**
 * Generic hook for fetching and caching widget data.
 *
 * To track live vs mock data, use the fetchFn pattern:
 * ```tsx
 * const { data, isLoading, error, refresh, isLive } = useWidgetData({
 *   widgetId: 'order-summary',
 *   instance: instance,
 *   fetchFn: async () => {
 *     try {
 *       return await fetchOrderSummary(); // API call
 *     } catch {
 *       return getMockData(); // fallback
 *     }
 *   },
 * });
 * ```
 *
 * Note: When fetchFn uses try/catch with mock fallback internally,
 * the hook always sees a successful promise. To properly track live vs mock,
 * widgets should use the `createLiveFetchFn` helper or set metadata.
 */
export function useWidgetData<T>({
  widgetId,
  instance,
  fetchFn,
  refreshIntervalOverride,
  enabled = true,
  staleTime = 10_000, // 10 seconds default
  queryOptions,
}: UseWidgetDataOptions<T>): UseWidgetDataResult<T> {
  // Track whether the last fetch was from a live API
  const isLiveRef = useRef(false);

  // Intervall: kodeoverstyring → widgetens valg → dashboardets valg → widgetens standard
  const dashboard = useOptionalDashboard();
  const intervalSeconds = refreshIntervalOverride ?? effectiveRefreshSeconds(instance, dashboard?.state.currentLayout);
  const refetchInterval = intervalSeconds > 0 ? intervalSeconds * 1000 : false;

  // Keep fetchFn always up-to-date via ref (so closure changes like reportId are captured)
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  // Stable wrapper that always delegates to the latest fetchFn
  const trackedFetchFn = useRef(async (): Promise<T> => {
    try {
      const result = await fetchFnRef.current();
      isLiveRef.current = true;
      return result;
    } catch {
      isLiveRef.current = false;
      throw new Error('Fetch failed');
    }
  }).current;

  const query = useQuery<T, Error>({
    queryKey: ['dashboard', widgetId, instance.instanceId],
    queryFn: trackedFetchFn,
    refetchInterval,
    staleTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
    refetchOnWindowFocus: false, // Dashboard is always visible, don't refetch on focus
    enabled,
    ...queryOptions,
  });

  // Manual refresh helper
  const refresh = () => {
    query.refetch();
  };

  // Convenience: first load = loading + no cached data
  const isFirstLoad = query.isLoading && !query.data;

  // Convenience: formatted last updated time
  const lastUpdatedText = query.dataUpdatedAt
    ? new Date(query.dataUpdatedAt).toLocaleTimeString('nb-NO', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '';

  return {
    ...query,
    refresh,
    isFirstLoad,
    lastUpdatedText,
    isLive: isLiveRef.current,
  };
}

/**
 * Simplified version for widgets that don't need instance-level configuration.
 * Useful for simple stat widgets.
 */
export function useSimpleWidgetData<T>(
  widgetId: string,
  fetchFn: () => Promise<T>,
  refreshIntervalMs: number = 30_000,
  enabled: boolean = true
): UseQueryResult<T, Error> & { refresh: () => void } {
  const query = useQuery<T, Error>({
    queryKey: ['dashboard', widgetId],
    queryFn: fetchFn,
    refetchInterval: refreshIntervalMs > 0 ? refreshIntervalMs : false,
    staleTime: 10_000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
    refetchOnWindowFocus: false,
    enabled,
  });

  return {
    ...query,
    refresh: () => query.refetch(),
  };
}

export default useWidgetData;
